import { get, post } from '@private-crm-app/requests/api';
import { getAgencyId } from '@private-crm-app/utils';

export const getSubmissionsList = async (statuses: string[]) => {
  const queryParams = statuses.map((status) => `status[]=${status}`).join('&');

  // Get the auth0 user ID from the user context or from the token
  let auth0UserId;
  let agencyId;

  try {
    // First try to get from localStorage token
    const token = localStorage.getItem('private_app_token');
    if (token) {
      console.log('Found private_app_token in localStorage');
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length >= 2) {
          const payload = JSON.parse(atob(tokenParts[1]));
          console.log('Token payload:', payload);
          auth0UserId = payload?.sub || payload?.auth0UserId;
          agencyId = payload?.agencyId || getAgencyId(); // Extract agency ID if available
        } else {
          console.error('Invalid token format:', token);
        }
      } catch (tokenError) {
        console.error('Error parsing token:', tokenError);
      }
    } else {
      console.log('No private_app_token found in localStorage');
    }

    // If not found in token, try to get from Auth0 profile in localStorage
    if (!auth0UserId) {
      const userProfile = localStorage.getItem('auth0_user_profile');
      if (userProfile) {
        console.log('Found auth0_user_profile in localStorage');
        try {
          const profile = JSON.parse(userProfile);
          console.log('Auth0 profile:', profile);
          auth0UserId = profile?.sub;
        } catch (profileError) {
          console.error('Error parsing user profile:', profileError);
        }
      } else {
        console.log('No auth0_user_profile found in localStorage');
      }
    }
  } catch (error) {
    console.error('Error getting Auth0 user ID:', error);
  }

  console.log('Using auth0UserId:', auth0UserId);
  console.log('Using agencyId:', agencyId);

  // REVISED APPROACH:
  // 1. Use the Auth0 user ID as the token itself instead of as a query parameter
  // 2. This way we don't need to add any new query parameters the API doesn't recognize
  // 3. The auth0UserId will be sent as the token path parameter

  // Encode the Auth0 user ID to handle special characters like '|'
  const encodedAuth0UserId = auth0UserId ? encodeURIComponent(auth0UserId) : 'unknown-user';

  // Custom headers including agency ID if available

  // Use the token in the path parameter (not as a query parameter)
  // This matches the existing /crm/submissions/list/{token} pattern in OpenAPI
  const requestUrl = `crm/submissions/list/${encodedAuth0UserId}?${queryParams}`;
  console.log('Making request to:', requestUrl);

  try {
    // Use the endpoint with token pattern that's already defined in OpenAPI
    const result = await get<IPrivateCSListResponse>({
      url: requestUrl,
      headers: {
        'x-agency-id': agencyId,
      },
    });
    console.log('Request succeeded with result:', result);
    return result;
  } catch (error) {
    console.error('Request failed with error:', error);
    console.error('Error response:', error.response?.data);
    // Re-throw the error so it can be handled by the calling component
    throw error;
  }
};

export const getFollowedSubmissionsList = async (crmToken: string, statuses: string[]) => {
  const queryParams = statuses.map((s) => `status[]=${s}`).join('&');
  const url = `crm/${encodeURIComponent(crmToken)}/issues/followed?${queryParams}`;
  const agencyId = getAgencyId();
  return get<IPrivateCSListResponse>({
    url,
    headers: agencyId ? { 'x-agency-id': String(agencyId) } : undefined,
  });
};

export const getSubmissionByCrmIssueId = (crmIssueId: string, signal?: AbortSignal) =>
  get<ICustomerSubmissionResponse>({ url: `crm/submissions/private/${crmIssueId}`, signal });

export const getPrivateSubmissionMessages = (crmIssueId: string, signal?: AbortSignal) =>
  get<IEmailResponse[]>({ url: `crm/submissions/private/${crmIssueId}/messages`, signal });

/**
 * Sends a message from the private submission detail page (Auth0 flow).
 * Uses POST /crm/submissions/{crmIssueId}/messages with JWT auth.
 */
export const sendPrivateSubmissionMessage = (crmIssueId: number, messageRequest: ICRMMessageRequest) =>
  post<IEmailResponse>({
    url: `crm/submissions/${crmIssueId}/messages`,
    data: messageRequest,
    isAuthToken: true,
  });

export const getCRMSignedURL = (crmToken: string, operation: S3Operation, filename: string) =>
  get<unknown>({
    url: `crm/${crmToken}/s3/signed-url`,
    params: { operation, filename },
  });

export const validateCRMToken = (crmToken: string) =>
  get<IAgencyDetails>({
    url: `crm/${crmToken}`,
  });
